import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Heading,
  Text,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Portal,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Skeleton,
  useBreakpointValue,
  Flex,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import {
  Plus,
  SignOut,
  House,
  MapPin,
  ChartLineUp,
  CaretRight,
  Gear,
  User,
  Bell
} from '@phosphor-icons/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

export default function DashboardHeader({ onSignOut, userName, isLoading }) {
  const navigate = useNavigate();
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const textSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const spacing = useBreakpointValue({ base: 2, md: 3 });

  const quickActions = [
    {
      label: isMobile ? "Add Site" : "Add New Site",
      icon: MapPin,
      onClick: () => navigate('/create-site')
    },
    {
      label: isMobile ? "Stats" : "View Statistics",
      icon: ChartLineUp,
      onClick: () => navigate('/stats')
    },
    {
      label: isMobile ? "Settings" : "Account Settings",
      icon: Gear,
      onClick: () => navigate('/account')
    }
  ];

  if (isLoading) {
    return (
      <Box mb={6}>
        <VStack align="stretch" spacing={4}>
          <Skeleton height="24px" width="120px" />
          <Skeleton height="48px" width="80%" />
          <Skeleton height="24px" width="60%" />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Skeleton height="48px" />
            <Skeleton height="48px" />
          </SimpleGrid>
        </VStack>
      </Box>
    );
  }

  return (
    <Box mb={6} role="banner">
      {/* Breadcrumb Navigation - Updated styles */}
      <Breadcrumb
        mb={4}
        spacing="8px"
        separator={<Icon as={CaretRight} color="text.muted" />} // Use semantic token
        fontFamily="body" // Use body font
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            as={RouterLink}
            to="/"
            color="text.link" // Use themed link color
            _hover={{ textDecoration: 'underline', color: 'text.link' }} // Standard link hover
          >
            <HStack spacing={1}>
              <Icon as={House} />
              <Text>Home</Text>
            </HStack>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <Text color="text.muted">Dashboard</Text> {/* Use semantic token */}
        </BreadcrumbItem>
      </Breadcrumb>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
        <VStack align="start" spacing={3}> {/* Adjusted spacing */}
          <HStack spacing={3}> {/* Adjusted spacing */}
            <Badge variant="journal"> {/* Use journal variant */}
              Researcher Portal
            </Badge>
            {userName && (
              <Badge variant="journal"> {/* Use journal variant */}
                <HStack spacing={1}>
                  <Icon as={User} />
                  <Text>{userName}</Text>
                </HStack>
              </Badge>
            )}
          </HStack>
          
          <Heading
            as="h1"
            size={headingSize}
            fontFamily="heading" // Ensure heading font
            color="text.default" // Use default text color
            // Removed bgGradient and bgClip
          >
            Megalithic Research Dashboard
          </Heading>
          
          <Text fontSize={textSize} color="text.muted"> {/* Use semantic token */}
            {isMobile ? "Track discoveries and explore sites" : "Track your contributions, analyze discoveries, and explore ancient sites"}
          </Text>
        </VStack>
        <HStack
          spacing={3} // Adjusted spacing
          justify={{ base: "flex-start", md: "flex-end" }}
          wrap="wrap"
          gap={3} // Adjusted gap
        >
          <Menu>
            <MenuButton
              as={Button}
              variant="journal" // Use journal variant
              leftIcon={<Plus weight="bold" />}
              size={buttonSize}
            >
              {isMobile ? "Actions" : "Quick Actions"}
            </MenuButton>
            <Portal>
              <MenuList bg="bg.surface" borderColor="border.default"> {/* Style MenuList */}
                {quickActions.map((action) => (
                  <MenuItem
                    key={action.label}
                    icon={<Icon as={action.icon} />}
                    onClick={action.onClick}
                    _hover={{ bg: 'bg.accent' }} // Themed hover
                    _focus={{ bg: 'bg.accent' }} // Themed focus
                    bg="transparent" // Ensure transparent base
                  >
                    {action.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          <HStack spacing={2}>
            <IconButton
              aria-label="View notifications"
              icon={<Icon as={Bell} />}
              variant="ghost" // Use ghost variant for subtle icon buttons
              size="md"
              color="text.muted" // Use muted color
              onClick={() => navigate('/notifications')}
            />
            
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="User menu"
                icon={<Icon as={User} />}
                variant="ghost" // Use ghost variant
                size="md"
                color="text.muted" // Use muted color
              />
              <Portal>
                <MenuList bg="bg.surface" borderColor="border.default"> {/* Style MenuList */}
                  <MenuItem
                    icon={<Icon as={User} />}
                    onClick={() => navigate('/account')}
                    _hover={{ bg: 'bg.accent' }}
                    _focus={{ bg: 'bg.accent' }}
                    bg="transparent"
                  >
                    Account Settings
                  </MenuItem>
                  <MenuDivider borderColor="border.light" /> {/* Themed divider */}
                  <MenuItem
                    icon={<Icon as={SignOut} />}
                    onClick={onSignOut}
                    color="error" // Use error color for sign out
                    _hover={{ bg: 'bg.accent' }}
                    _focus={{ bg: 'bg.accent' }}
                    bg="transparent"
                  >
                    Sign Out
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </HStack>
      </SimpleGrid>
    </Box>
  );
}

function Icon({ as: IconComponent, ...props }) {
  if (!IconComponent) return null;
  return <IconComponent weight="bold" size={20} {...props} />;
}
